import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import {
  fetchAllMaterials as fetchDwhMaterialsPage,
  fetchAllPrinters as fetchPrintersPage,
} from '$api/evoAPIs'

const useMaterialMapping = () => {
  const { data: allMaterials, isLoading: isLoadingMaterials } = useQuery(
    ['allDwhMaterials'],
    async () => {
      let materials = []
      let currentPage = 1
      let hasMorePages = true
      while (hasMorePages) {
        const pageData = await fetchDwhMaterialsPage({
          queryKey: ['dwhMaterialsPage', { page: currentPage, size: 100 }],
        })
        if (pageData?.items?.length > 0) {
          materials = materials.concat(pageData.items)
          hasMorePages = materials.length < pageData.total
          currentPage++
        } else {
          hasMorePages = false
        }
      }
      return materials
    },
    {
      staleTime: 10 * 60 * 1000,
    },
  )

  const { data: printerMaterialsSet, isLoading: isLoadingPrinters } = useQuery(
    ['allPrinterMaterialsSet'],
    async () => {
      let printers = []
      let currentPage = 1
      let hasMorePages = true
      while (hasMorePages) {
        const printerPage = await fetchPrintersPage({
          queryKey: ['printersPage', { page: currentPage, size: 100 }],
        })
        if (printerPage?.items?.length > 0) {
          printers = printers.concat(printerPage.items)
          hasMorePages = printers.length < printerPage.total
          currentPage++
        } else {
          hasMorePages = false
        }
      }

      return printers.reduce((acc, printer) => {
        printer.materials?.forEach((materialId) => {
          acc.add(materialId)
        })
        return acc
      }, new Set())
    },
    {
      staleTime: 10 * 60 * 1000,
    },
  )

  const materialsWithAvailability = useMemo(() => {
    if (!allMaterials || !printerMaterialsSet) {
      return []
    }
    return allMaterials.map((material) => ({
      ...material,
      printerAvailable: printerMaterialsSet.has(material.id),
    }))
  }, [allMaterials, printerMaterialsSet])

  const materialsById = useMemo(() => {
    if (!materialsWithAvailability) return {}
    return materialsWithAvailability.reduce((acc, material) => {
      acc[material.id] = material
      return acc
    }, {})
  }, [materialsWithAvailability])

  return {
    data: materialsWithAvailability,
    isLoading: isLoadingMaterials || isLoadingPrinters,
    materialsById,
  }
}

export default useMaterialMapping
